"use client";

import { useMemo, useRef, useState } from "react";
import ContactAlert from "../../_components/alerts/ContactAlert";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "../../_components/security/TurnstileWidget";
import { FieldError } from "../../_components/ui/FieldError";
import { useRequestDemoForm } from "../../hooks/useRequestDemoForm";
import type { RequestDemoFormValues } from "../../hooks/useRequestDemoForm";
import countries from "../../../public/countries.json";
import states from "../../../public/states.json";

export function RequestDemoForm() {
  const [alertStatus, setAlertStatus] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null);

  const sortedCountries = useMemo(
    () =>
      [...countries].sort((a, b) => {
        if (a.name === "United States") {
          return -1;
        }
        if (b.name === "United States") {
          return 1;
        }
        return a.name.localeCompare(b.name);
      }),
    [],
  );

  const { form } = useRequestDemoForm({
    onSubmit: requestDemo,
  });

  async function requestDemo(data: RequestDemoFormValues) {
    try {
      const response = await fetch("/api/requestDemo", {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.message || "Failed to request demo");
      }

      const result = await response.json();

      if (result.success) {
        setAlertStatus("Success");
        setShowAlert(true);
        setAlertMessage(result.message);
        turnstileRef.current?.reset();
        return result;
      } else {
        throw new Error(result.message || "Request demo failed");
      }
    } catch (error) {
      setAlertStatus("Failed");
      setShowAlert(true);
      setAlertMessage(
        "Our servers are temporarily unavailable. Please try again later or contact us via email or phone",
      );
      turnstileRef.current?.reset();

      return {
        success: false,
        message: error instanceof Error ? error.message : "An error occurred",
      };
    }
  }

  function closeAlert() {
    setShowAlert(false);
  }

  return (
    <div className="page padded">
      {showAlert && (
        <ContactAlert
          showAlert={showAlert}
          alertMessage={alertMessage}
          alertStatus={alertStatus}
          registeredURL={null}
          registeredSerial={null}
          closeAlert={closeAlert}
        >
          <p>{alertMessage}</p>
        </ContactAlert>
      )}
      <form
        className="demo-form"
        noValidate={true}
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div className="form-section">
          <div className="section-title">
            <h4>Company Information</h4>
          </div>

          <form.Field name="company">
            {(field) => (
              <div className="input-group">
                <label htmlFor={field.name}>Company Name</label>
                <input
                  type="text"
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  placeholder="Company Name"
                  aria-invalid={!field.state.meta.isValid}
                  onBlur={field.handleBlur}
                  onChange={(event) => field.handleChange(event.target.value)}
                />
                <FieldError errors={field.state.meta.errors} />
              </div>
            )}
          </form.Field>

          <form.Field name="companyWebsiteUrl">
            {(field) => (
              <div className="sh2z3zJM">
                <label htmlFor={field.name}>Company Website URL</label>
                <input
                  type="text"
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  placeholder="Company Website"
                  autoComplete="new-password"
                  onChange={(event) => field.handleChange(event.target.value)}
                />
              </div>
            )}
          </form.Field>

          <div className="form-two-column">
            <form.Field name="address">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Street Address</label>
                  <input
                    type="text"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="123 Avenue"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
            <form.Field name="address2">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Suite</label>
                  <input
                    type="text"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="Suite 42"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
          </div>
          <div className="form-three-column">
            <form.Field name="city">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>City</label>
                  <input
                    type="text"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="City"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
            <form.Field name="state">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>State</label>
                  <select
                    value={field.state.value}
                    name={field.name}
                    id={field.name}
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option
                        value={state.abbreviation}
                        key={state.abbreviation}
                      >
                        {state.name}
                      </option>
                    ))}
                  </select>
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
            <form.Field name="zip">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Zip</label>
                  <input
                    type="text"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="12345"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
          </div>
          <div className="form-two-column">
            <form.Field name="country">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Country</label>
                  <select
                    value={field.state.value}
                    name={field.name}
                    id={field.name}
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  >
                    <option value="">Select Country</option>
                    {sortedCountries.map((country) => (
                      <option value={country.id} key={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
            <form.Field name="website">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Website</label>
                  <input
                    type="text"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="Website URL"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
          </div>
        </div>

        <div className="form-section">
          <div className="section-title">
            <h4>Primary Contact Info</h4>
          </div>
          <div className="form-two-column">
            <form.Field name="fname">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Full Name</label>
                  <input
                    type="text"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="John Smith"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
            <form.Field name="title">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Job Position</label>
                  <input
                    type="text"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="Job Position"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
            <form.Field name="phone">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Office Number</label>
                  <input
                    type="tel"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="123-456-7890"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
            <form.Field name="mphone">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Mobile Number</label>
                  <input
                    type="tel"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="123-456-7890"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
            <form.Field name="email">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Email</label>
                  <input
                    type="email"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="abcd@gmail.com"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
            <form.Field name="numLocs">
              {(field) => (
                <div className="input-group">
                  <label htmlFor={field.name}>Number of Locations</label>
                  <input
                    type="number"
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    placeholder="1"
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>
          </div>
        </div>

        <form.Field name="comment">
          {(field) => (
            <div className="input-group textarea">
              <label htmlFor={field.name}>Comments</label>
              <textarea
                id={field.name}
                name={field.name}
                value={field.state.value}
                placeholder="Comments"
                aria-invalid={!field.state.meta.isValid}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
              />
              <FieldError errors={field.state.meta.errors} />
            </div>
          )}
        </form.Field>

        <div style={{ marginBlock: "1rem" }}>
          <form.Field name="turnstileToken">
            {(field) => (
              <div className="input-group">
                <FieldError errors={field.state.meta.errors} />
                <TurnstileWidget
                  action="request-demo"
                  ref={turnstileRef}
                  onTokenChange={(token) => field.handleChange(token)}
                  onTokenClear={() => field.handleChange("")}
                />
              </div>
            )}
          </form.Field>
        </div>

        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <button
              className="form-submit"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          )}
        </form.Subscribe>
      </form>
    </div>
  );
}
